'use client';

import * as React from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export interface MultiSelectOption {
  label: string;
  value: any;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: any[];
  onChange: (value: any[]) => void;
  getUnknownValueLabel?: (value: any) => string;
  ariaLabel?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value = [],
  onChange,
  getUnknownValueLabel = (unknownValue) => String(unknownValue),
  ariaLabel,
  placeholder = 'Select options...',
  className,
  disabled = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [popoverWidth, setPopoverWidth] = React.useState<number | undefined>(undefined);

  // Update popover width to match trigger
  React.useEffect(() => {
    if (triggerRef.current) {
      setPopoverWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  // Preserve values that are no longer in the option directory so users can
  // see and remove stale selections instead of silently submitting them.
  const selectedOptions = React.useMemo(() => {
    return value.map((selectedValue) => (
      options.find((option) => option.value === selectedValue) || {
        value: selectedValue,
        label: getUnknownValueLabel(selectedValue),
      }
    ));
  }, [getUnknownValueLabel, options, value]);

  // Toggle option selection
  const toggleOption = React.useCallback((optionValue: any) => {
    const newValues = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange(newValues);
  }, [value, onChange]);

  // Clear all selections
  const handleClear = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  }, [onChange]);

  // Remove single selection
  const handleRemove = React.useCallback((optionValue: any, e: React.MouseEvent) => {
    e.stopPropagation();
    const newValues = value.filter((v) => v !== optionValue);
    onChange(newValues);
  }, [value, onChange]);

  // Display text for trigger button
  const displayText = React.useMemo(() => {
    if (selectedOptions.length === 0) {
      return placeholder;
    }
    if (selectedOptions.length === 1) {
      return selectedOptions[0].label;
    }
    return `${selectedOptions.length} selected`;
  }, [selectedOptions, placeholder]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "h-9 w-full justify-between font-normal min-h-9",
            !value.length && "text-muted-foreground",
            className
          )}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {selectedOptions.length > 0 ? (
              <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-hidden">
                {selectedOptions.length <= 2 ? (
                  // Show badges for 1-2 selections
                  selectedOptions.map((option) => (
                    <Badge
                      key={option.value}
                      variant="secondary"
                      className="text-xs px-2 py-0.5 h-6 truncate max-w-[120px]"
                    >
                      <span className="truncate">{option.label}</span>
                      <X
                        className="ml-1.5 h-3 w-3 cursor-pointer shrink-0"
                        onClick={(e) => handleRemove(option.value, e)}
                      />
                    </Badge>
                  ))
                ) : (
                  // Show count for 3+ selections
                  <span className="truncate text-sm">{displayText}</span>
                )}
              </div>
            ) : (
              <span className="truncate text-sm">{displayText}</span>
            )}
          </div>
          <div className="flex items-center gap-1 ml-2 shrink-0">
            {selectedOptions.length > 0 && (
              <X
                className="h-4 w-4 opacity-50 hover:opacity-100 cursor-pointer"
                onClick={handleClear}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-full"
        style={{ width: popoverWidth }}
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search options..." className="h-9" />
          <CommandList className="max-h-[300px]">
            <CommandEmpty>No option found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = value.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => toggleOption(option.value)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="flex-1">{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
